import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import getDecryptData from "../../../helpers/getDecryptData";
import { getPublicHomeData, isExternalLink, normalizeAssetUrl, normalizePublicLink } from "../homeDataCache";

const FALLBACK_LINKS = [
  { id: "jobs", text: "Find Jobs", link: "/jobs", children: [] },
  { id: "advice", text: "Career Advice", link: "/career-advice", children: [] },
  { id: "about", text: "About", link: "/about-us", children: [] },
  { id: "contact", text: "Contact", link: "/contact-us", children: [] },
];

const MenuLink = ({ item, className, onNavigate }) => {
  const link = normalizePublicLink(item?.link);
  if (isExternalLink(link)) {
    return <a className={className} href={link} target={item?.target || "_self"} rel={item?.target === "_blank" ? "noopener noreferrer" : undefined} onClick={onNavigate}>{item?.text}</a>;
  }
  return <Link className={className} to={link} target={item?.target || "_self"} onClick={onNavigate}>{item?.text}</Link>;
};

const MenuTree = ({ items, level = 0, openMenus, toggleMenu, onNavigate, pathname }) => (
  <ul className={`ynb-menu-level ynb-menu-level-${level}`}>
    {items.map((item, index) => {
      const children = Array.isArray(item?.children) ? item.children.filter((child) => child?.text) : [];
      const key = String(item?.id ?? `${level}-${index}-${item?.text}`);
      const active = !isExternalLink(item?.link) && normalizePublicLink(item?.link) === pathname;
      return (
        <li className={`${children.length ? "has-children" : ""} ${openMenus.includes(key) ? "is-expanded" : ""}`} key={key}>
          <div className="ynb-menu-row">
            <MenuLink item={item} className={active ? "active" : ""} onNavigate={children.length ? undefined : onNavigate} />
            {children.length > 0 && <button type="button" onClick={() => toggleMenu(key)} aria-expanded={openMenus.includes(key)} aria-label={`Toggle ${item.text} submenu`}><span>⌄</span></button>}
          </div>
          {children.length > 0 && <MenuTree items={children} level={level + 1} openMenus={openMenus} toggleMenu={toggleMenu} onNavigate={onNavigate} pathname={pathname} />}
        </li>
      );
    })}
  </ul>
);

const Header = () => {
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [homeData, setHomeData] = useState({});
  const [open, setOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [data, user] = await Promise.all([getPublicHomeData(), Promise.resolve(getDecryptData())]);
        setHomeData(data || {});
        setSession(user || null);
      } catch (error) {
        setSession(getDecryptData() || null);
      }
    };
    load();
    const sync = (event) => setSession(event?.detail || null);
    window.addEventListener("ynb-auth-changed", sync);
    return () => window.removeEventListener("ynb-auth-changed", sync);
  }, []);

  useEffect(() => { setOpen(false); setOpenMenus([]); }, [location.pathname]);

  const dashboardLink = session?.type === 0 ? "/dashboard" : session?.type === 2 ? "/job-seeker/dashboard" : session?.type === 1 || session?.type === 3 ? "/recruiter/dashboard" : "/login";
  const menuItems = Array.isArray(homeData?.header) && homeData.header.length ? homeData.header : FALLBACK_LINKS;
  const logoUrl = normalizeAssetUrl(homeData?.meta_logo);
  const closeNavigation = () => { setOpen(false); setOpenMenus([]); };
  const toggleMenu = (key) => setOpenMenus((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);

  return (
    <header className="ynb-header">
      <div className="portal-container ynb-header-inner">
        <Link to="/" className={`ynb-logo ${logoUrl ? "has-image" : ""}`} aria-label={`${homeData?.meta_title || "Ynbjobs"} home`}>
          {logoUrl ? <img src={logoUrl} alt={homeData?.meta_title || "Ynbjobs"} /> : <>Ynb<span>jobs</span></>}
        </Link>
        <nav className={`ynb-nav ${open ? "is-open" : ""}`} aria-label="Main navigation">
          <MenuTree items={menuItems} openMenus={openMenus} toggleMenu={toggleMenu} onNavigate={closeNavigation} pathname={location.pathname} />
          <div className="ynb-nav-mobile-actions"><Link to={session ? dashboardLink : "/login"} className="ynb-link-button">{session ? "Dashboard" : "Sign In"}</Link>{!session && <Link to="/register" className="ynb-primary-button">Get Started</Link>}</div>
        </nav>
        <div className="ynb-header-actions"><Link to={session ? dashboardLink : "/login"} className="ynb-link-button">{session ? "Dashboard" : "Sign In"}</Link>{!session && <Link to="/register" className="ynb-primary-button">Get Started</Link>}</div>
        <button className="ynb-menu-button" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-label="Toggle navigation"><span></span><span></span><span></span></button>
      </div>
    </header>
  );
};

export default Header;
